/**
 * Stocktake — TanStack Query hooks. Repointed to the `/api` surface
 * (#1151 PR-3): the service now resolves unwrapped payloads (ADR-0006
 * §5 envelope stripped by `http`), so the hooks carry the inner
 * shapes directly — no `ApiResponse<T>` wrapper.
 *
 * Stale-time choices:
 *   - list: 30s (operator-actionable — short window so an "open new
 *     → list refresh" loop feels live).
 *   - detail: 15s (count-entry page reads from this; on a tablet
 *     where a peer might be counting concurrently we want short
 *     freshness for the variance review).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { resourceKeys } from '@resource-kit/controllers';
import { queryKeys } from '@lib/queryKeys';
import { stocktakeResource } from '@features/stocktake';
import {
  stocktakeAPI,
  type StocktakeDetail,
  type StocktakeLine,
  type RecordCountInput,
  type CommitStocktakeResult,
  type CancelStocktakeResult,
} from '@/services';

// The list + open hooks moved to `features/stocktake` with the converged list
// page (#1418); only the count/review workflow hooks remain here.
const DETAIL_STALE_MS = 15_000;

// A status transition (count/commit/cancel) changes the badge the framework
// LIST renders under `['stocktakes', …]`, while the detail read this page uses
// lives under the legacy singular `['stocktake', …]`. Both must be invalidated
// after a mutation — a plain `['stocktake']` invalidation is a positional-tuple
// no-op against the framework list (#1418). Keyed off `stocktakeResource.slug`
// so the two can't drift.
const frameworkKeys = resourceKeys(stocktakeResource.host.slug);

export function useStocktakeDetail(id: string | undefined) {
  return useQuery<StocktakeDetail>({
    queryKey: queryKeys.stocktake.detail(id),
    queryFn: () => {
      if (!id) {
        // Defensive — guarded by `enabled` below. If we got here, the
        // caller wired the hook without an id check; fail-loud per
        // EQD §3 so the bug surfaces immediately rather than via a
        // silently-empty render.
        throw new Error('useStocktakeDetail: id is required');
      }
      return stocktakeAPI.getById(id);
    },
    enabled: Boolean(id),
    staleTime: DETAIL_STALE_MS,
  });
}

/**
 * Thrown by {@link useRecordCount} when the per-line loop fails after
 * at least one line was already persisted. Carries the partial progress
 * (`savedCount` of `totalCount`) so the count page can tell the operator
 * how much survived rather than implying the whole batch was lost. The
 * originating failure is preserved on `cause` — the error is re-raised
 * with context, never swallowed.
 */
export class PartialRecordCountError extends Error {
  readonly savedCount: number;
  readonly totalCount: number;
  readonly cause: unknown;

  constructor(savedCount: number, totalCount: number, cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'PartialRecordCountError';
    this.savedCount = savedCount;
    this.totalCount = totalCount;
    this.cause = cause;
  }
}

/**
 * Record a batch of counts. The `/api` `count` endpoint takes ONE line
 * per call, so a "Save counts" batch loops per line here — the page
 * still submits a single `mutateAsync({ lines })`. Lines are posted
 * sequentially (D1 write path; keeps ordering deterministic and avoids
 * a burst of concurrent transactions on the same stocktake). Returns
 * the recorded lines in submission order.
 *
 * Partial failure: if a line fails mid-batch, the already-persisted
 * lines are real writes on the backend. We surface that as a
 * {@link PartialRecordCountError} (progress + preserved cause) instead
 * of a bare reject, and invalidation runs on `onSettled` so the detail
 * cache refreshes to show the lines that DID save — even on failure.
 */
export function useRecordCount(stocktakeId: string) {
  const qc = useQueryClient();
  return useMutation<StocktakeLine[], Error, { lines: RecordCountInput[] }>({
    mutationFn: async ({ lines }) => {
      // Guard the empty-string fallback at the call boundary, same
      // pattern as useRecordReceipt from procurement. Without this
      // guard a missing param would POST to `.../stocktakes//count`
      // and 404 silently.
      if (!stocktakeId) {
        throw new Error('useRecordCount: stocktakeId is required');
      }
      const recorded: StocktakeLine[] = [];
      for (const line of lines) {
        try {
          recorded.push(await stocktakeAPI.recordCount(stocktakeId, line));
        } catch (err) {
          // Re-raise with partial-progress context. `recorded.length`
          // lines were already persisted before this one failed.
          throw new PartialRecordCountError(recorded.length, lines.length, err);
        }
      }
      return recorded;
    },
    onSettled: () => {
      // Invalidate on BOTH success and failure: a mid-batch failure
      // still persisted earlier lines, so the detail cache is stale
      // either way. The framework list also refreshes — a first count
      // flips the badge open → counted.
      qc.invalidateQueries({ queryKey: frameworkKeys.all() });
      qc.invalidateQueries({ queryKey: queryKeys.stocktake.all() });
    },
  });
}

export function useCommitStocktake(stocktakeId: string) {
  const qc = useQueryClient();
  return useMutation<CommitStocktakeResult, Error, void>({
    mutationFn: () => {
      if (!stocktakeId) {
        throw new Error('useCommitStocktake: stocktakeId is required');
      }
      return stocktakeAPI.commit(stocktakeId);
    },
    onSuccess: () => {
      // Framework list (status badge) + legacy singular detail namespace.
      qc.invalidateQueries({ queryKey: frameworkKeys.all() });
      qc.invalidateQueries({ queryKey: queryKeys.stocktake.all() });
    },
  });
}

export function useCancelStocktake(stocktakeId: string) {
  const qc = useQueryClient();
  return useMutation<CancelStocktakeResult, Error, void>({
    mutationFn: () => {
      if (!stocktakeId) {
        throw new Error('useCancelStocktake: stocktakeId is required');
      }
      return stocktakeAPI.cancel(stocktakeId);
    },
    onSuccess: () => {
      // Framework list (status badge) + legacy singular detail namespace.
      qc.invalidateQueries({ queryKey: frameworkKeys.all() });
      qc.invalidateQueries({ queryKey: queryKeys.stocktake.all() });
    },
  });
}
